/**
 * Reusable presentational primitives shared across admin + student pages.
 *
 * No third-party UI framework — these are thin React wrappers over the CSS
 * classes defined in index.css. Keeping primitives small lets pages compose
 * them naturally; we don't try to wrap every native element.
 */
import type { ReactNode } from "react";

// ---------- PageHeader ----------

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle && <div className="page-subtitle">{subtitle}</div>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

// ---------- Card ----------

export function Card({
  title,
  subtitle,
  children,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card">
      {title && <h2 className="card-title">{title}</h2>}
      {subtitle && <div className="card-subtitle">{subtitle}</div>}
      {children}
    </section>
  );
}

// ---------- Alert ----------

export type AlertKind = "error" | "success" | "warning" | "info";

export function Alert({
  kind = "info",
  children,
}: {
  kind?: AlertKind;
  children: ReactNode;
}) {
  return (
    <div className={`alert alert-${kind}`} role={kind === "error" ? "alert" : "status"}>
      {children}
    </div>
  );
}

// ---------- StatusBadge ----------

export type StatusValue =
  | "draft"
  | "active"
  | "closed"
  | "archived"
  | "not_started"
  | "submitted"
  | "graded"
  | "running"
  | "stopped"
  | "expired"
  | "failed";

const STATUS_LABELS: Record<StatusValue, string> = {
  draft: "Draft",
  active: "Active",
  closed: "Closed",
  archived: "Archived",
  not_started: "Not started",
  submitted: "Submitted",
  graded: "Graded",
  running: "Running",
  stopped: "Stopped",
  expired: "Expired",
  failed: "Failed",
};

export function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <span>—</span>;
  const key = status.toLowerCase().replace(/-/g, "_") as StatusValue;
  const label = STATUS_LABELS[key] ?? status;
  const cls =
    key === "not_started" ? "badge-not-started" : `badge-${key}`;
  return (
    <span className={`badge ${cls}`} aria-label={`Status: ${label}`}>
      {label}
    </span>
  );
}

// ---------- EmptyState ----------

export function EmptyState({
  title,
  hint,
}: {
  title: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <strong>{title}</strong>
      {hint && <div>{hint}</div>}
    </div>
  );
}

// ---------- LoadingState ----------

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="loading-state" role="status" aria-live="polite">
      {label}
    </div>
  );
}
